/**
 * SAP CPI Monitoring Dashboard
 * Copyright (c) 2026 David Jahn
 * Contact: david.jahnxx@gmail.com
 */
import { useState, useEffect, useCallback, useRef, useMemo, Component } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import "./i18n";
import { ThemeProvider, CssBaseline } from "@mui/material";
import MuiButton from "@mui/material/Button";
import MuiTextField from "@mui/material/TextField";
import MuiAvatar from "@mui/material/Avatar";
import MuiIconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import SearchRounded from "@mui/icons-material/SearchRounded";
import Box from "@mui/material/Box";

// === Design System (Phase 0) ===
import { FONT, DARK } from "./theme/tokens";
import { buildMuiTheme } from "./theme/muiTheme";
import { buildCSS } from "./theme/cssVariables";
import { ThemeCtx } from "./theme/ThemeContext";
import { api, v, getToken, setToken, hatCicdZugang, isGlobalAdmin, isSuperAdmin, resolveVisibleSpace } from "./utils/helpers";
import useDashboardStore from "./store/useDashboardStore";
import useDialogFocus from "./hooks/useDialogFocus";
import useBranding from "./hooks/useBranding";
import { rememberSpace } from "./utils/transportApprovalPreferences";

// Backward-compatible alias used in this file
const setTk = setToken;
const canonicalizeSpaceUrl = (space, requestedId) => {
  if (!space || space.id === requestedId) return;
  const parts = window.location.pathname.split("/");
  if (parts[1] !== "space" || parts[2] !== requestedId) return;
  parts[2] = space.id;
  window.history.replaceState(window.history.state, "", parts.join("/") + window.location.search + window.location.hash);
};


// === Page Components (Phase 1 - extracted from monolith) ===
import { NavIcon, BrandMark } from "./components/AppBrand";
import SpaceNavigation from "./components/SpaceNavigation";
import SidebarUserMenu from "./components/SidebarUserMenu";
import LoginPage from "./pages/LoginPage";
import SpacesHome from "./pages/SpacesHome";
import SpaceDetail from "./pages/SpaceDetail";
import Dashboard from "./pages/Dashboard";
import InviteAccept from "./pages/InviteAccept";
import SettingsModal from "./pages/SettingsModal";
import ForcePasswordChange from "./pages/ForcePasswordChange";
import AlertingPage from "./pages/AlertingPage";
import AuditLog from "./pages/AuditLog";
import TransportApprovals from "./pages/TransportApprovals";
import PastebinPage from "./pages/PastebinPage";
import PartnerDirectory from "./pages/PartnerDirectory";
import PipelineTopology from "./pages/PipelineTopology";
import PixelArt from "./pages/PixelArt";
import DokuPage from "./pages/DokuPage";
import TransportV2Page from "./pages/transport-v2/TransportV2Page";
import BenutzerAnlegen from "./pages/BenutzerAnlegen";
import BenutzerVerwalten from "./pages/BenutzerVerwalten";
import CicdSetup from "./pages/CicdSetup";
import CicdPipeline from "./pages/CicdPipeline";
import AiIflowBuilder from "./pages/AiIflowBuilder";
import AiInsights from "./pages/AiInsights";
import Monitoring from "./pages/Monitoring";
import ChatBubble from "./components/ChatBubble";
import ChatPanel from "./components/ChatPanel";
import { useSSE } from "./hooks/useSSE";

// === TanStack Query - globale Instanz (immer aktiv, aber Queries nur wenn experimentalCaching=true) ===
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// SSE-Connector: Muss innerhalb von QueryClientProvider gerendert werden
function SSEConnector() {
  useSSE();
  return null;
}

// === Error Boundary (catches runtime crashes → shows error instead of black screen) ===
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("React Error Boundary caught:", error, info); }
  render() {
    if (this.state.hasError) {
      return <div style={{ minHeight: "100vh", background: "#050810", color: "#edf0f7", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT }}>
        <div style={{ textAlign: "center", maxWidth: 500, padding: 40 }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <h2 style={{ marginBottom: 8 }}>{i18n.t("navigation:fehler.titel")}</h2>
          <p style={{ color: "#8b9dc3", marginBottom: 16 }}>{this.state.error?.message || i18n.t("navigation:fehler.unbekannt")}</p>
          <pre style={{ textAlign: "left", background: "#0e1422", padding: 16, borderRadius: 8, fontSize: 11, overflow: "auto", maxHeight: 200, color: "#ef4444" }}>{this.state.error?.stack?.split("\n").slice(0, 8).join("\n")}</pre>
          <button onClick={() => { this.setState({ hasError: false }); window.location.href = "/"; }} style={{ marginTop: 16, padding: "10px 24px", borderRadius: 8, border: "none", background: "#6366f1", color: "#fff", fontSize: 14, cursor: "pointer" }}>{i18n.t("navigation:fehler.zurStartseite")}</button>
        </div>
      </div>;
    }
    return this.props.children;
  }
}

export default function App(){
  const{t}=useTranslation(["navigation","gemeinsam"]);
  const[user,setUserState]=useState(null);const[checking,setChecking]=useState(true);
  const[page,setPage]=useState("spaces");const[spaces,setSpaces]=useState([]);const[activeSpace,setActiveSpace]=useState(null);const[activeInst,setActiveInst]=useState(null);
  const[initialSpacesLoading,setInitialSpacesLoading]=useState(true);
  useEffect(()=>{
    if (["space", "dashboard"].includes(page) && spaces.some(space=>space.id===activeSpace?.id)) {
      rememberSpace(user?.id, activeSpace.id);
    }
  },[user?.id,page,activeSpace,spaces]);
  const[spacesError,setSpacesError]=useState(null);
  const sessionRef=useRef({user:null,generation:0});
  const spacesRequest=useRef(0);
  const cicdStatusRequest=useRef(0);
  const spacesLoaded=useRef(false);
  const hoverCache=useRef({});
  const hoverRequests=useRef(new Map());
  const refreshingSpaces=useRef(false);
  // Invalidate outstanding work before rendering another account or the login page.
  const setUser=useCallback(value=>{
    const previous=sessionRef.current.user;
    const next=typeof value==="function"?value(previous):value;
    if(previous?.id!==next?.id){
      sessionRef.current.generation++;
      spacesRequest.current++;
      spacesLoaded.current=false;
      setSpaces([]);setInitialSpacesLoading(true);setSpacesError(null);
      hoverCache.current={};hoverRequests.current.clear();
      setHoverInstances({});setCicdStatus({tokenSet:false,spaces:{}});
      setActiveSpace(null);setActiveInst(null);
      queryClient.clear();
    }
    sessionRef.current.user=next;
    setUserState(next);
  },[]);
  useEffect(()=>()=>{sessionRef.current.generation++;spacesRequest.current++;},[]);
  useEffect(()=>{
    const onSessionChange=event=>{
      if(event.key!=="cpi_token"&&event.key!==null)return;
      if(event.storageArea&&event.storageArea!==window.localStorage)return;
      // All page state belongs to the authenticated account in this tab.
      setUser(null);setChecking(true);
      window.location.reload();
    };
    window.addEventListener("storage",onSessionChange);
    return()=>window.removeEventListener("storage",onSessionChange);
  },[setUser]);
  const[pdPreselectedInst,setPdPreselectedInst]=useState(null);const[pdPreselectedPid,setPdPreselectedPid]=useState(null);
  const[targetFlowName,setTargetFlowName]=useState(null);
  const[targetTab,setTargetTab]=useState(null);
  // dashboardSubTab haelt den aktiven Subtab fuer Sidebar-Highlight stabil.
  // targetTab wird von Dashboard.jsx nach Konsum geleert - dashboardSubTab nicht.
  const[dashboardSubTab,setDashboardSubTab]=useState("monitor");
  const[showMenu,setShowMenu]=useState(false);const[showSettings,setShowSettings]=useState(false);const[mobileNavOpen,setMobileNavOpen]=useState(false);
  const { branding, brandingError, reloadBranding } = useBranding(user);
  // CI/CD-Bindung je Space (zentral geladen, einmal pro Session/Refresh, an SpacesHome+SpaceDetail durchgereicht).
  // Each Space supplies pipelineReady independently of the user's personal tokenSet.
  const[cicdStatus,setCicdStatus]=useState({tokenSet:false,spaces:{}});
  const[chatOpen,setChatOpen]=useState(false);
  const canUseAssistant=isSuperAdmin(user)&&!user?.mustChangePassword;
  useEffect(()=>{if(!canUseAssistant)setChatOpen(false);},[canUseAssistant]);
  const[spacesHover,setSpacesHover]=useState(false);const[hoverSpaceId,setHoverSpaceId]=useState(null);const[hoverInstances,setHoverInstances]=useState({});
  hoverCache.current=hoverInstances;
  // Sidebar-Tree: aufklappbare Spaces > Instanzen > Monitor/Content
  const[treeExpSpaces,setTreeExpSpaces]=useState({});
  const[treeExpInsts,setTreeExpInsts]=useState({});
  const inviteToken=window.location.pathname.startsWith("/invite/")?window.location.pathname.split("/invite/")[1]:null;

  // === URL Routing (#6) ===
  const pageRef=useRef(page);pageRef.current=page;
  const buildUrl=(pg,space,inst)=>{
    if(pg==="spaces")return"/";
    if(pg==="space"&&space)return`/space/${space.id}`;
    if(pg==="dashboard"&&inst)return`/space/${space?.id||activeSpace?.id||inst.spaceId||"_"}/${inst.id}`;
    if(pg==="ai-builder")return"/ai-builder";
    if(pg==="alerting")return"/alerting";
    if(pg==="audit")return"/audit";
    if(pg==="approvals")return"/approvals";
    if(pg==="pastebin")return"/pastebin";
    if(pg==="pd")return"/pd";
    if(pg==="pipeline")return"/pipeline";
    if(pg==="pixel-art")return"/pixel-art";
    if(pg==="doku")return"/doku";
    if(pg==="transport")return"/transport";
    if(pg==="transport-v2")return"/transport-v2";
    if(pg==="benutzer-anlegen")return"/benutzer-anlegen";
    if(pg==="benutzer-verwalten")return"/benutzer-verwalten";
    if(pg==="cicd-setup")return"/cicd-setup";
    if(pg==="cicd-pipeline")return"/cicd-pipeline";
    if(pg==="ai-insights")return"/ai-insights";
    if(pg==="monitoring")return"/monitoring";
    if(pg==="invite"&&inviteToken)return`/invite/${inviteToken}`;
    return"/";
  };
  const navigate=(pg,space,inst,opts)=>{
    if(opts?.flowName)console.log("[Nav] →",pg,"flowName:",opts.flowName,"inst:",inst?.id);
    setPage(pg);if(space!==undefined)setActiveSpace(space);if(inst!==undefined)setActiveInst(inst);
    if(opts?.flowName)setTargetFlowName(opts.flowName);
    if(opts?.tab)setTargetTab({tab:opts.tab,nonce:Date.now()});
    // dashboardSubTab stabil halten fuer Sidebar-Highlight (unabhaengig von targetTab-Clear)
    if(pg==="dashboard"){if(opts?.tab==="content"||opts?.flowName)setDashboardSubTab("content");else if(opts?.tab==="monitor")setDashboardSubTab("monitor");}
    else if(pg!=="dashboard")setDashboardSubTab("monitor");
    let url=buildUrl(pg,space||activeSpace,inst||activeInst);
    if(opts?.flowName&&pg==="dashboard")url+="/content/"+encodeURIComponent(opts.flowName);
    else if(opts?.tab==="content"&&pg==="dashboard")url+="/content";
    window.history.pushState({page:pg,tab:opts?.tab},document.title,url);
    setMobileNavOpen(false);
  };

  // Parse URL on load
  useEffect(()=>{
    const path=window.location.pathname;
    const parts=path.split("/").filter(Boolean);
    if(parts[0]==="chat"){window.history.replaceState({},"","/");}
    else if(parts[0]==="alerting"){setPage("alerting");}
    else if(parts[0]==="audit"){setPage("audit");}
    else if(parts[0]==="approvals"){setPage("approvals");}
    else if(parts[0]==="pastebin"){setPage("pastebin");}
    else if(parts[0]==="pd"){setPage("pd");}
    else if(parts[0]==="pipeline"){setPage("pipeline");}
    else if(parts[0]==="pixel-art"){setPage("pixel-art");}
    else if(parts[0]==="doku"){setPage("doku");}
    else if(parts[0]==="transport"){setPage("transport-v2");}
    else if(parts[0]==="transport-v2"){setPage("transport-v2");}
    else if(parts[0]==="benutzer-anlegen"){setPage("benutzer-anlegen");}
    else if(parts[0]==="benutzer-verwalten"){setPage("benutzer-verwalten");}
    else if(parts[0]==="cicd-setup"){setPage("cicd-setup");}
    else if(parts[0]==="cicd-pipeline"){setPage("cicd-pipeline");}
    // Altes Lesezeichen /transport-freigabe wird auf die neue Freigabe-Seite gemappt.
    else if(parts[0]==="transport-freigabe"){setPage("approvals");}
    else if(parts[0]==="ai-insights"){setPage("ai-insights");}
    else if(parts[0]==="monitoring"){setPage("monitoring");}
    else if(parts[0]==="ai-builder"){setPage("ai-builder");}
    else if(parts[0]==="invite"&&parts[1]){setPage("invite");}
    else if(parts[0]==="space"&&parts[1]){
      setPage(parts[2]?"dashboard":"space");
      const params=new URLSearchParams(window.location.search);
      const flowParam=params.get("flow");
      const pathFlow=(parts[3]==="content"&&parts[4])?decodeURIComponent(parts[4]):null;
      if(flowParam||pathFlow)setTargetFlowName(flowParam||pathFlow);
      // Fix B: targetTab beim Direktaufruf der content-URL setzen, damit Sidebar-Highlight stimmt
      if(parts[2]&&parts[3]==="content"){setTargetTab({tab:"content",nonce:Date.now()});setDashboardSubTab("content");}
      else if(parts[2])setDashboardSubTab("monitor");
      window.__pendingRoute={spaceId:parts[1],instId:parts[2]||null};
    }
  },[]);

  // Listen for navigate-to-partner-directory events from child components
  useEffect(()=>{
    const onNavPd=(e)=>{setPdPreselectedInst(e.detail?.instId||null);setPdPreselectedPid(e.detail?.pid||null);setPage("pd");window.history.pushState({page:"pd"},"","/pd");setMobileNavOpen(false);};
    window.addEventListener("cpi-nav-pd",onNavPd);return()=>window.removeEventListener("cpi-nav-pd",onNavPd);
  },[]);

  useEffect(()=>{
    let l=document.querySelector("link[rel~='icon']");
    if(!l){l=document.createElement("link");l.rel="icon";document.head.appendChild(l);}
    // Assign the default explicitly so a previous account's icon cannot remain cached.
    l.href=branding.faviconUrl || `${process.env.PUBLIC_URL || ""}/icon-iio.svg`;
    if(branding.title)document.title=branding.title;
  },[branding]);
  useEffect(()=>{
    let cancelled=false;
    const token=getToken();
    if(!token){setChecking(false);return;}
    api("/api/auth/me").then(result=>{if(!cancelled&&getToken()===token)setUser(result);})
      .catch(()=>{if(!cancelled&&getToken()===token)setTk(null);})
      .finally(()=>{if(!cancelled)setChecking(false);});
    return()=>{cancelled=true;};
  },[setUser]);

  // Zustand Store: experimentalCaching Flag aus Settings laden
  useEffect(()=>{
    if(!user)return;
    let cancelled=false;
    api("/api/auth/settings").then(s=>{
      if(!cancelled)useDashboardStore.getState().setExperimentalCaching(s.experimentalCaching||false);
    }).catch(()=>{});
    return()=>{cancelled=true;};
  },[user]);

  // CI/CD-Bindung je Space einmal zentral laden (guenstig, keine GitHub-Calls).
  // Wird von SpacesHome, SpaceDetail und der Sidebar geteilt genutzt.
  const cicdSessionGeneration=sessionRef.current.generation;
  const loadCicdStatus=useCallback(()=>{
    if(!user||user.id!==sessionRef.current.user?.id||cicdSessionGeneration!==sessionRef.current.generation)return;
    const generation=sessionRef.current.generation;
    const token=getToken();
    const request=++cicdStatusRequest.current;
    const current=()=>generation===sessionRef.current.generation&&token===getToken()&&request===cicdStatusRequest.current;
    return api("/api/cicd/spaces-status")
      .then(r=>{if(current())setCicdStatus({tokenSet:!!r?.tokenSet,spaces:r?.spaces||{}});})
      .catch(()=>{});
  },[user,cicdSessionGeneration]);
  useEffect(()=>{loadCicdStatus();},[loadCicdStatus]);

  const loadSpaces=useCallback(async()=>{
    if(!sessionRef.current.user||sessionRef.current.user.mustChangePassword)return;
    const generation=sessionRef.current.generation;
    const request=++spacesRequest.current;
    hoverRequests.current.clear();refreshingSpaces.current=true;
    const token=getToken();
    const current=()=>generation===sessionRef.current.generation&&request===spacesRequest.current&&token===getToken();
    setSpacesError(null);
    setInitialSpacesLoading(!spacesLoaded.current);
    let sp;
    try{
      sp=await api("/api/spaces");
      if(!current())return;
      if(!Array.isArray(sp))throw Object.assign(new Error("Invalid spaces response"), { code: "INVALID_SPACES_RESPONSE" });
      spacesLoaded.current=true;
      // A changed count invalidates the list, including cached empty Spaces.
      hoverCache.current=Object.fromEntries(sp.filter(space=>Array.isArray(hoverCache.current[space.id])
        && Number.isInteger(space.instanceCount)&&hoverCache.current[space.id].length===space.instanceCount)
        .map(space=>[space.id,hoverCache.current[space.id]]));
      setHoverInstances(hoverCache.current);refreshingSpaces.current=false;
      setSpaces(sp);setInitialSpacesLoading(false);
    }
    catch(e){if(current()){refreshingSpaces.current=false;setSpacesError(e);setInitialSpacesLoading(false);if(window.__pendingRoute)setPage("spaces");}return;}
    if(!window.__pendingRoute)return;
    const{spaceId,instId}=window.__pendingRoute;delete window.__pendingRoute;
    if(!spaceId){return;}
    let space=resolveVisibleSpace(sp,spaceId);
    canonicalizeSpaceUrl(space,spaceId);
    if(!space&&instId){
      for(const s of sp){
        try{
          const insts=await api(`/api/spaces/${s.id}/instances`);
          if(!current())return;
          const found=insts.find(i=>i.id===instId);
          if(found){space=s;setActiveSpace(s);setActiveInst(found);setPage("dashboard");return;}
        }catch{if(!current())return;}
      }
    }
    if(!space){console.warn("[Route] Space",spaceId,"not found");setPage("spaces");return;}
    setActiveSpace(space);
    if(!instId){setPage("space");return;}
    try{
      const insts=await api(`/api/spaces/${space.id}/instances`);
      if(!current())return;
      const inst=insts.find(i=>i.id===instId);
      if(inst){setActiveInst(inst);setPage("dashboard");}
      else{console.warn("[Route] Instance",instId,"not found in space",space.id);setPage("space");}
    }catch(e){if(current()){console.error("[Route] instances failed:",e);setPage("space");}}
  },[]);
  useEffect(()=>{if(user&&!user.mustChangePassword){loadSpaces();if(inviteToken)setPage("invite");}},[user,loadSpaces,inviteToken]);

  useEffect(()=>{
    const onPop=()=>{
      const path=window.location.pathname;const parts=path.split("/").filter(Boolean);
      if(parts[0]==="chat")window.history.replaceState({},"","/");
      // Top-Level-Pages: targetTab/targetFlowName resetten, damit beim Zurueckklick auf eine Instanz nicht der alte Subtab haengen bleibt
      const topLevel=["ai-builder","alerting","audit","approvals","pastebin","pd","pipeline","pixel-art","doku","transport","transport-v2","benutzer-anlegen","benutzer-verwalten","cicd-setup","cicd-pipeline","ai-insights","monitoring"];
      // Die alte Transport-App ist entfernt. Lesezeichen auf /transport zeigen
      // weiter, statt ins Leere zu laufen.
      if(parts[0]==="transport"){setPage("transport-v2");setTargetTab(null);setTargetFlowName(null);setDashboardSubTab("monitor");return;}
      // Altes Lesezeichen /transport-freigabe -> neue Freigabe-Seite (Route "approvals").
      if(parts[0]==="transport-freigabe"){setPage("approvals");setTargetTab(null);setTargetFlowName(null);setDashboardSubTab("monitor");return;}
      if(topLevel.includes(parts[0])){setPage(parts[0]);setTargetTab(null);setTargetFlowName(null);setDashboardSubTab("monitor");return;}
      if(parts[0]==="space"&&parts[2]){
        const flow=(parts[3]==="content"&&parts[4])?decodeURIComponent(parts[4]):null;
        setTargetFlowName(flow||null);
        // Fix A: Subtab aus URL ableiten, sonst zeigt Dashboard nach Back den falschen Tab
        if(parts[3]==="content"){setTargetTab({tab:"content",nonce:Date.now()});setDashboardSubTab("content");}
        else{setTargetTab({tab:"monitor",nonce:Date.now()});setDashboardSubTab("monitor");}
        const existingSpace=resolveVisibleSpace(spaces,parts[1]);
        canonicalizeSpaceUrl(existingSpace,parts[1]);
        if(existingSpace&&activeInst?.id===parts[2]){setActiveSpace(existingSpace);setPage("dashboard");}
        else{window.__pendingRoute={spaceId:parts[1],instId:parts[2]};setPage("dashboard");loadSpaces();}
      }
      else if(parts[0]==="space"&&parts[1]){
        const existingSpace=resolveVisibleSpace(spaces,parts[1]);
        canonicalizeSpaceUrl(existingSpace,parts[1]);
        if(existingSpace){setActiveSpace(existingSpace);setActiveInst(null);setPage("space");setTargetTab(null);setTargetFlowName(null);}
        else{setPage("spaces");setActiveSpace(null);setActiveInst(null);setTargetTab(null);setTargetFlowName(null);}
      }
      else{setPage("spaces");setActiveSpace(null);setActiveInst(null);setTargetTab(null);setTargetFlowName(null);}
    };
    window.addEventListener("popstate",onPop);return()=>window.removeEventListener("popstate",onPop);
  },[spaces,activeInst,loadSpaces]);
  const logout=()=>{delete window.__pendingRoute;dismissSearch();setTk(null);setUser(null);setSpaces([]);setPage("spaces");setActiveSpace(null);setActiveInst(null);setTargetTab(null);setTargetFlowName(null);setDashboardSubTab("monitor");setPdPreselectedInst(null);setPdPreselectedPid(null);setChatOpen(false);setShowMenu(false);setMobileNavOpen(false);window.history.pushState({},"","/");};

  // Hover instances loader for nav dropdown
  const loadHoverInstances=useCallback(async(spaceId)=>{
    if(refreshingSpaces.current||hoverCache.current[spaceId]||hoverRequests.current.has(spaceId))return;
    const generation=sessionRef.current.generation;
    const refresh=spacesRequest.current;
    const token=getToken();
    const request={};
    hoverRequests.current.set(spaceId,request);
    const current=()=>generation===sessionRef.current.generation&&refresh===spacesRequest.current
      &&token===getToken()&&hoverRequests.current.get(spaceId)===request;
    try{
      const insts=await api(`/api/spaces/${spaceId}/instances`);
      if(!current()||!Array.isArray(insts))return;
      hoverCache.current={...hoverCache.current,[spaceId]:insts};
      setHoverInstances(hoverCache.current);
    }catch{/* Keep an unsuccessful read uncached so opening the Space can retry. */}
    finally{if(hoverRequests.current.get(spaceId)===request)hoverRequests.current.delete(spaceId);}
  },[]);
  useEffect(()=>{
    spaces.forEach(space=>{if(treeExpSpaces[space.id])loadHoverInstances(space.id);});
  },[spaces,treeExpSpaces,loadHoverInstances]);
  const iconSessionGeneration=sessionRef.current.generation;
  const onInstanceUpdated=useCallback(updated=>{
    if(iconSessionGeneration!==sessionRef.current.generation)return;
    if(!updated?.id||!Object.hasOwn(updated,"icon"))return;
    const update=inst=>inst?.id===updated.id?{...inst,icon:updated.icon}:inst;
    setHoverInstances(previous=>Object.fromEntries(Object.entries(previous).map(([id,instances])=>[id,instances.map(update)])));
    setActiveInst(update);
  },[iconSessionGeneration]);

  const[theme,setTheme]=useState(()=>localStorage.getItem("cpi_theme")||"dark");
  // Theme-Wechsel mit weichem Crossfade: View-Transitions-API wenn verfuegbar,
  // sonst kurzzeitige Farb-Transitions auf allen Elementen (CSS .theme-fade).
  const toggleTheme=()=>{
    const t=theme==="dark"?"light":"dark";
    const apply=()=>{setTheme(t);localStorage.setItem("cpi_theme",t);};
    if(document.startViewTransition){document.startViewTransition(apply);}
    else{
      document.documentElement.classList.add("theme-fade");
      apply();
      setTimeout(()=>document.documentElement.classList.remove("theme-fade"),380);
    }
  };
  // Sync data-theme to <html> so MUI Portal dialogs inherit CSS variables
  useEffect(()=>{document.documentElement.setAttribute("data-theme",theme);},[theme]);
  // Hintergrund-Parallax: Papier-Textur-Ebene driftet beim Scrollen sanft mit (GPU-transform).
  useEffect(()=>{
    if(typeof window==="undefined")return;
    if(window.matchMedia&&window.matchMedia("(prefers-reduced-motion: reduce)").matches)return;
    const SIZE=22; let ticking=false;
    const raf=window.requestAnimationFrame?window.requestAnimationFrame.bind(window):(f)=>setTimeout(f,16);
    const onScroll=()=>{ if(ticking)return; ticking=true; raf(()=>{
      const el=document.querySelector(".iio-bgfx");
      if(el){ const sy=window.pageYOffset||document.documentElement.scrollTop||0; el.style.transform="translate3d(0,"+(-((sy*0.25)%SIZE))+"px,0)"; }
      ticking=false;
    }); };
    window.addEventListener("scroll",onScroll,{passive:true});
    return ()=>window.removeEventListener("scroll",onScroll);
  },[]);
  const muiTheme=useMemo(()=>buildMuiTheme(theme),[theme]);
  const[sidebarOpen,setSidebarOpen]=useState(true);
  // - Sidebar v2: Rail-Modus, Spaces-Gruppe, User-Menü, gleitender Marker -
  const[sbRail,setSbRail]=useState(()=>{try{return localStorage.getItem("iio_sb_rail")==="1";}catch{return false;}});
  const toggleSbRail=()=>{setSbRail(r=>{const n=!r;try{localStorage.setItem("iio_sb_rail",n?"1":"0");}catch{}return n;});};
  const[sbGrpOpen,setSbGrpOpen]=useState(true);
  const[sbUserMenu,setSbUserMenu]=useState(false);
  const sbUserRef=useRef(null);
  const [compactViewport,setCompactViewport]=useState(()=>window.matchMedia("(max-width: 900px)").matches);
  useEffect(()=>{
    const media=window.matchMedia("(max-width: 900px)");
    const update=()=>{setCompactViewport(media.matches);if(!media.matches)setMobileNavOpen(false);};
    media.addEventListener("change",update);return()=>media.removeEventListener("change",update);
  },[]);
  useEffect(()=>{
    if(compactViewport&&!mobileNavOpen&&sbUserMenu){
      setSbUserMenu(false);
      requestAnimationFrame(()=>document.querySelector(".iio-topbar-mobile-toggle")?.focus());
    }
  },[compactViewport,mobileNavOpen,sbUserMenu]);
  useEffect(()=>{
    if(!mobileNavOpen||!compactViewport)return;
    setChatOpen(false);
    const sidebar=document.querySelector(".iio-sidebar");
    const toggle=document.querySelector(".iio-topbar-mobile-toggle");
    const previousOverflow=document.body.style.overflow;
    document.body.style.overflow="hidden";
    sidebar?.querySelector("button")?.focus({preventScroll:true});
    const keys=event=>{
      if(event.target.closest?.(".ci-user-popover"))return;
      if(event.key==="Escape"){event.preventDefault();setMobileNavOpen(false);toggle?.focus();}
      if(event.key==="Tab"){
        const items=[...sidebar.querySelectorAll("button:not(:disabled), [tabindex='0']"),toggle].filter(el=>el&&el.getClientRects().length&&!el.closest("[hidden], [inert]"));
        const first=items[0],last=items[items.length-1];
        if(event.shiftKey&&document.activeElement===first){event.preventDefault();last?.focus();}
        else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first?.focus();}
      }
    };
    document.addEventListener("keydown",keys);
    return()=>{document.body.style.overflow=previousOverflow;document.removeEventListener("keydown",keys);if(sidebar?.contains(document.activeElement))toggle?.focus({preventScroll:true});};
  },[mobileNavOpen,compactViewport]);
  const sbScrollRef=useRef(null);
  const sbMarkerRef=useRef(null);
  const[sbFades,setSbFades]=useState({top:false,bot:false});
  const updateSbFades=useCallback(()=>{
    const sc=sbScrollRef.current;if(!sc)return;
    setSbFades(p=>{
      const top=sc.scrollTop>4, bot=sc.scrollTop<sc.scrollHeight-sc.clientHeight-4;
      return (p.top===top&&p.bot===bot)?p:{top,bot};
    });
  },[]);
  // Marker via Bounding-Rects relativ zum Scroll-Inhalt (offsetTop wäre durch
  // verschachtelte position:relative-Eltern versetzt). Leaf vor Item bevorzugen.
  const repositionSbMarker=useCallback(()=>{
    const sc=sbScrollRef.current,mk=sbMarkerRef.current;
    if(!sc||!mk)return;
    const active=[...sc.querySelectorAll(".iio-sb2-leaf.is-active, .iio-sb2-item.is-active")]
      .filter(el=>!el.closest("[inert], [hidden]")&&getComputedStyle(el).visibility!=="hidden");
    const el=active.find(el=>el.classList.contains("iio-sb2-leaf"))||active[0];
    if(!el||el.getBoundingClientRect().height<4){mk.style.opacity="0";return;}
    const sr=sc.getBoundingClientRect(),er=el.getBoundingClientRect();
    const h=Math.max(16,Math.min(34,Math.round(er.height)-8));
    mk.style.height=h+"px";
    mk.style.transform="translateY("+((er.top-sr.top)+sc.scrollTop+(er.height-h)/2)+"px)";
    mk.style.opacity="1";
  },[]);
  useEffect(()=>{
    const sc=sbScrollRef.current;
    if(!sc||typeof ResizeObserver==="undefined")return;
    const observer=new ResizeObserver(()=>{repositionSbMarker();updateSbFades();});
    sc.querySelectorAll(".iio-sb2-sec").forEach(section=>observer.observe(section));
    return()=>observer.disconnect();
  },[checking,user?.id,user?.mustChangePassword,repositionSbMarker,updateSbFades]);
  // Nur bei Layout-relevanten Aenderungen neu positionieren (nicht bei jedem Render)
  useEffect(()=>{
    const raf=requestAnimationFrame(()=>{repositionSbMarker();updateSbFades();});
    const t=setTimeout(()=>{repositionSbMarker();updateSbFades();},360);
    return()=>{cancelAnimationFrame(raf);clearTimeout(t);};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[page,dashboardSubTab,activeInst,sbGrpOpen,treeExpSpaces,treeExpInsts,sbRail,compactViewport,spaces,hoverInstances]);
  const[spacesExpanded,setSpacesExpanded]=useState(true);
  const[searchQuery,setSearchQuery]=useState("");const[searchResults,setSearchResults]=useState(null);const[searchLoading,setSearchLoading]=useState(false);
  const searchTimeout=useRef(null);
  const searchGeneration=useRef(0);
  useEffect(()=>()=>{clearTimeout(searchTimeout.current);searchGeneration.current++;},[]);
  const dismissSearch=()=>{searchGeneration.current++;clearTimeout(searchTimeout.current);setSearchResults(null);setSearchLoading(false);};

  // Global search
  const runSearch=async(q,generation)=>{
    if(generation!==searchGeneration.current)return;
    if(!q.trim()){setSearchResults(null);setSearchLoading(false);return;}
    setSearchLoading(true);
    try{
      const results={iflows:[],packages:[],instances:[]};
      for(const sp of spaces){
        try{const insts=await api(`/api/spaces/${sp.id}/instances`);
          if(generation!==searchGeneration.current)return;
          for(const inst of insts){
            if(inst.name.toLowerCase().includes(q.toLowerCase())) results.instances.push({...inst,spaceName:sp.name,spaceId:sp.id});
            try{const flows=await api(`/api/${inst.id}/iflows`);
              if(generation!==searchGeneration.current)return;
              flows.filter(f=>f.Name?.toLowerCase().includes(q.toLowerCase())).forEach(f=>results.iflows.push({...f,instanceName:inst.name,instanceId:inst.id,spaceId:sp.id,spaceName:sp.name}));
            }catch{}
          }
        }catch{}
      }
      if(generation===searchGeneration.current)setSearchResults(results);
    }catch{}if(generation===searchGeneration.current)setSearchLoading(false);
  };
  const handleSearch=(val)=>{setSearchQuery(val);clearTimeout(searchTimeout.current);
    const generation=++searchGeneration.current;
    searchTimeout.current=setTimeout(()=>runSearch(val,generation),500);
  };

  useDialogFocus();
  const CSS = buildCSS();

  if(checking)return<QueryClientProvider client={queryClient}><ErrorBoundary><ThemeProvider theme={muiTheme}><CssBaseline/><div data-theme={theme}><style>{CSS}</style><div style={{minHeight:"100vh",background:v("bg"),display:"flex",alignItems:"center",justifyContent:"center",color:v("text-3")}}>{t("gemeinsam:zustand.laedt")}</div></div></ThemeProvider></ErrorBoundary></QueryClientProvider>;
  if(!user&&page==="pixel-art")return<QueryClientProvider client={queryClient}><ErrorBoundary><ThemeProvider theme={muiTheme}><CssBaseline/><ThemeCtx.Provider value={{theme,toggle:toggleTheme}}><div data-theme={theme}><style>{CSS}</style><div style={{minHeight:"100vh",background:v("bg")}}><PixelArt isGuest/></div></div></ThemeCtx.Provider></ThemeProvider></ErrorBoundary></QueryClientProvider>;
  if(!user)return<QueryClientProvider client={queryClient}><ErrorBoundary><ThemeProvider theme={muiTheme}><CssBaseline/><ThemeCtx.Provider value={{theme,toggle:toggleTheme}}><div data-theme={theme}><style>{CSS}</style><LoginPage onLogin={setUser} inviteToken={inviteToken}/></div></ThemeCtx.Provider></ThemeProvider></ErrorBoundary></QueryClientProvider>;
  if(user.mustChangePassword)return<QueryClientProvider client={queryClient}><ErrorBoundary><ThemeProvider theme={muiTheme}><CssBaseline/><ThemeCtx.Provider value={{theme,toggle:toggleTheme}}><div data-theme={theme}><style>{CSS}</style><ForcePasswordChange onDone={()=>setUser({...user,mustChangePassword:false})}/></div></ThemeCtx.Provider></ThemeProvider></ErrorBoundary></QueryClientProvider>;

  return(
    <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
    <SSEConnector/>
    <ThemeProvider theme={muiTheme}><CssBaseline/>
    <ThemeCtx.Provider value={{theme,toggle:toggleTheme}}>
    <div data-theme={theme} className="iio">
      <style>{CSS}</style>
      {/* Hintergrund-FX: Papier-Textur-Ebene (fixiert, hinter dem Inhalt, Scroll-Parallax) */}
      <div className="iio-bgfx" aria-hidden="true"/>

      {/* === MOBILE OVERLAY BACKDROP === */}

      <div className={"iio-app"+(sbRail&&!compactViewport?" is-rail":"")}>
      <div className={"iio-mobile-backdrop"+(mobileNavOpen?" is-open":"")} onClick={()=>setMobileNavOpen(false)}/>

        {/* === SIDEBAR (v2: Navigation scrollbar | User fix)
             Die Marke sitzt in der Kopfleiste, siehe .iio-topbar-lead. === */}
        <aside inert={compactViewport&&!mobileNavOpen?"":undefined} className={"iio-sidebar"+(mobileNavOpen?" is-open":"")}>

          {/* Zone 2: scrollbare Navigation mit gleitendem Marker.
              Die Verlaufskanten liegen als Auflage AUF dem Mantel, nicht im
              Scroller - nur so enden sie exakt auf der Trennlinie zum Benutzer. */}
          <div className="iio-sb2-navwrap">
          <nav className="iio-sb2-scroll" ref={sbScrollRef} onScroll={updateSbFades}>
            <span className="iio-sb2-marker" ref={sbMarkerRef} style={{transform:"translateY(0)",opacity:0}}/>

            <div className="iio-sb2-sec">
              <div className="iio-sb2-label">{t("gruppe.arbeitsbereich")}</div>

              {/* Spaces: Gruppe auf-/zuklappbar, Kopf navigiert zur Übersicht */}
              <SpaceNavigation spaces={spaces} cicdStatus={cicdStatus} groupOpen={sbGrpOpen} setGroupOpen={setSbGrpOpen}
                expandedSpaces={treeExpSpaces} setExpandedSpaces={setTreeExpSpaces}
                expandedInstances={treeExpInsts} setExpandedInstances={setTreeExpInsts}
                instances={hoverInstances} loadInstances={loadHoverInstances}
                activeSpace={activeSpace} activeInst={activeInst} page={page} dashboardSubTab={dashboardSubTab}
                onOverview={()=>{setSbGrpOpen(true);setTargetTab(null);setTargetFlowName(null);setDashboardSubTab("monitor");navigate("spaces",null,null);loadSpaces();}}
                onSpace={sp=>{setTargetTab(null);setTargetFlowName(null);navigate("space",sp,null);}}
                onInstance={(sp,inst,tab)=>navigate("dashboard",sp,inst,{tab})}/>

              {[
                {key:"ai-builder",label:t("eintrag.kiIflowBuilder"),active:page==="ai-builder",onClick:()=>navigate("ai-builder",null,null),icon:<NavIcon name="ai-builder"/>},
                {key:"pipeline",label:t("eintrag.pipeline"),active:page==="pipeline",onClick:()=>navigate("pipeline",null,null),icon:<NavIcon name="pipeline"/>},
                ...(window.location.hostname!=="app.tpase.de"||localStorage.getItem("featureTransportPage")==="true" ? [{key:"transport-v2",label:t("eintrag.transport"),active:page==="transport-v2",onClick:()=>navigate("transport-v2",null,null),icon:<NavIcon name="transport-v2"/>}] : []),
              ].map(n=>(
                <button key={n.key} data-nav={n.key} type="button" className={"iio-sb2-item"+(n.active?" is-active":"")} title={n.label} onClick={()=>{n.onClick();setMobileNavOpen(false);}}>
                  <span className="ic">{n.icon}</span><span className="lbl">{n.label}</span>
                </button>
              ))}
            </div>

            <div className="iio-sb2-sec">
              <div className="iio-sb2-label">{t("gruppe.governance")}</div>
              {[
                {key:"alerting",label:t("eintrag.alerting"),active:page==="alerting",onClick:()=>navigate("alerting",null,null),icon:<NavIcon name="alerting"/>},
                {key:"pd",label:t("eintrag.partnerDirectory"),active:page==="pd",onClick:()=>navigate("pd",null,null),icon:<NavIcon name="pd"/>},
                {key:"audit",label:t("eintrag.auditLog"),active:page==="audit",onClick:()=>navigate("audit",null,null),icon:<NavIcon name="audit"/>},
                {key:"doku",label:t("eintrag.dokumentation"),active:page==="doku",onClick:()=>navigate("doku",null,null),icon:<NavIcon name="doku"/>},
              ].map(n=>(
                <button key={n.key} data-nav={n.key} type="button" className={"iio-sb2-item"+(n.active?" is-active":"")} title={n.label} onClick={()=>{n.onClick();setMobileNavOpen(false);}}>
                  <span className="ic">{n.icon}</span><span className="lbl">{n.label}</span>
                </button>
              ))}
            </div>

            <div className="iio-sb2-sec">
              <div className="iio-sb2-label">{t("gruppe.administration")}</div>
              {[
                ...(isSuperAdmin(user)?[
                {key:"monitoring",label:t("monitoring:kopf.titel"),active:page==="monitoring",onClick:()=>navigate("monitoring",null,null),icon:<NavIcon name="monitoring"/>},
                ]:[]),
                ...(isGlobalAdmin(user)?[
                {key:"benutzer-anlegen",label:t("eintrag.benutzerAnlegen"),active:page==="benutzer-anlegen",onClick:()=>navigate("benutzer-anlegen",null,null),icon:<NavIcon name="benutzer-anlegen"/>},
                {key:"benutzer-verwalten",label:t("eintrag.benutzerVerwalten"),active:page==="benutzer-verwalten",onClick:()=>navigate("benutzer-verwalten",null,null),icon:<NavIcon name="benutzer-verwalten"/>},
                {key:"ai-insights",label:t("eintrag.kiInsights"),active:page==="ai-insights",onClick:()=>navigate("ai-insights",null,null),icon:<NavIcon name="ai-insights"/>},
                ]:[]),
                ...(user?[
                {key:"cicd-setup",label:t("eintrag.cicdSetup"),active:page==="cicd-setup",onClick:()=>navigate("cicd-setup",null,null),icon:<NavIcon name="cicd-setup"/>},
                ]:[]),
                ...(hatCicdZugang(user,spaces)?[
                {key:"cicd-pipeline",label:t("eintrag.cicdMonitor"),active:page==="cicd-pipeline",onClick:()=>navigate("cicd-pipeline",null,null),icon:<NavIcon name="cicd-pipeline"/>},
                // Transportfreigabe: eine einzige Sidebar-Zeile, Route "approvals".
                // Sichtbar fuer globale Admins UND Space-Admins (hatCicdZugang).
                {key:"approvals",label:t("eintrag.transportFreigabe"),active:page==="approvals",onClick:()=>navigate("approvals",null,null),icon:<NavIcon name="approvals"/>},
                ]:[]),
              ].map(n=>(
                <button key={n.key} data-nav={n.key} type="button" className={"iio-sb2-item"+(n.active?" is-active":"")} title={n.label} onClick={()=>{n.onClick();setMobileNavOpen(false);}}>
                  <span className="ic">{n.icon}</span><span className="lbl">{n.label}</span>
                </button>
              ))}
            </div>

          </nav>
            <div className={"iio-sb2-fade top"+(sbFades.top?" show":"")} aria-hidden="true"/>
            <div className={"iio-sb2-fade bot"+(sbFades.bot?" show":"")} aria-hidden="true"/>
          </div>

          {/* Zone 3: User fest verankert, Menü klappt nach oben */}
          <div className={"iio-sb2-foot"+(sbUserMenu?" open":"")}>
            <button ref={sbUserRef} id="sidebar-user-trigger" aria-haspopup="menu" aria-expanded={sbUserMenu} aria-controls={sbUserMenu?"sidebar-user-menu":undefined} type="button" className="iio-sb2-user" title={user.displayName||user.username}
              onClick={(e)=>{e.stopPropagation();setSbUserMenu(o=>!o);}}>
              <span className="ava">{user.displayName?.[0]?.toUpperCase()||"?"}</span>
              <span className="uinfo">
                <span className="uname">{user.displayName||user.username}</span>
                <span className="urole">{isSuperAdmin(user)?t("benutzer.rolleSuperadmin"):isGlobalAdmin(user)?t("benutzer.rolleAdmin"):t("benutzer.rolleBenutzer")}</span>
              </span>
              <svg className="uchev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
            </button>
            {sbUserMenu&&(
              <SidebarUserMenu anchorRef={sbUserRef} theme={theme} onClose={()=>setSbUserMenu(false)}>
                <button role="menuitem" type="button" onClick={()=>{setSbUserMenu(false);setMobileNavOpen(false);setShowSettings(true);}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                  {t("benutzer.einstellungen")}
                </button>
                <button role="menuitem" type="button" onClick={()=>{setSbUserMenu(false);toggleTheme();}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/></svg>
                  {t("benutzer.themaWechseln")}
                </button>
                <div className="sep" role="separator"/>
                <button role="menuitem" type="button" className="danger" onClick={()=>{setSbUserMenu(false);logout();}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="m16 17 5-5-5-5M21 12H9"/></svg>
                  {t("benutzer.abmelden")}
                </button>
              </SidebarUserMenu>
            )}
          </div>
        </aside>

        {/* === TOPBAR (durchgehend über die volle Breite) === */}
        <header className="iio-topbar">
          {/* Marke + Seitenleisten-Schalter. Beide Schalter meinen dasselbe Ziel
              (Navigation zeigen oder wegnehmen) und teilen sich denselben Platz -
              sichtbar ist immer nur einer, je nach Breite. */}
          <div className="iio-topbar-lead">
            <button type="button" className="iio-topbar-mobile-toggle" onClick={()=>setMobileNavOpen(o=>!o)} aria-label={t("seitenleiste.menue")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            </button>
            <button type="button" className="iio-topbar-rail-toggle" onClick={toggleSbRail} aria-pressed={sbRail} title={sbRail?t("seitenleiste.ausklappen"):t("seitenleiste.einklappen")} aria-label={sbRail?t("seitenleiste.ausklappen"):t("seitenleiste.einklappen")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M9 4v16"/></svg>
            </button>
            <button inert={compactViewport&&mobileNavOpen?"":undefined} type="button" className="iio-topbar-brand" onClick={()=>{setTargetTab(null);setTargetFlowName(null);setDashboardSubTab("monitor");navigate("spaces",null,null);loadSpaces();setMobileNavOpen(false);}} title={branding.title||"Integration in One"}>
              <span className="iio-topbar-logo">
                {branding.logoUrl
                  ? <img src={branding.logoUrl} alt=""/>
                  : <BrandMark/>}
              </span>
              <span className="iio-topbar-wm">
                <span className="w1">{branding.title||"Integration in One"}</span>
                {/* Der Untertitel kommt aus dem Branding. Hier stand fest
                    "Mission Control", der eingestellte Wert wurde also nie
                    angezeigt. Ist er leer, entfaellt die Zeile ganz, statt
                    3px Aussenabstand und eine leere Zeilenhoehe zu erzeugen. */}
                {branding.subtitle&&<span className="w2">{branding.subtitle}</span>}
              </span>
            </button>
          </div>

          {/* Steht ganz rechts neben Thema und Abmelden - geschoben vom
              auto-Margin in .iio-topbar-search, nicht von der Reihenfolge hier.
              Die bleibt so, damit die Tabulatorfolge der Leserichtung folgt. */}
          {/* Das Feld hat keine sichtbare Beschriftung, seinen Namen trug bisher
              allein der Platzhalter - und der verschwindet, sobald jemand tippt.
              aria-label traegt ihn dauerhaft, role="search" macht den Bereich
              als Sprungziel auffindbar. */}
          <div inert={compactViewport&&mobileNavOpen?"":undefined} className="iio-topbar-search iio-topbar-search--mui" role="search" style={{position:"relative"}}>
            <MuiTextField className="iio-topbar-search-field" variant="outlined" fullWidth size="small"
              onKeyDown={event=>{if(event.key==="Escape"){event.preventDefault();dismissSearch();}}}
              value={searchQuery} onChange={e=>handleSearch(e.target.value)} placeholder={t("suche.platzhalter")}
              inputProps={{ "aria-label": t("suche.platzhalter"), autoComplete: "off" }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded aria-hidden="true"/></InputAdornment> }}/>
            {searchResults&&searchQuery&&(
              <div className="iio-search-results">
                {searchResults.instances?.length>0&&<>
                  <div className="iio-search-section">{t("suche.instanzen")}</div>
                  {searchResults.instances.map(i=>(
                    <button key={i.id} className="iio-search-item" onClick={()=>{setSearchQuery("");setSearchResults(null);const sp=spaces.find(s=>s.id===i.spaceId);navigate("dashboard",sp,i);}}>
                      <span>{i.name}</span><span className="iio-search-meta">{i.spaceName}</span>
                    </button>
                  ))}
                </>}
                {searchResults.iflows?.length>0&&<>
                  <div className="iio-search-section">{t("suche.iflows")}</div>
                  {searchResults.iflows.slice(0,10).map((f,idx)=>(
                    <button key={idx} className="iio-search-item" onClick={()=>{setSearchQuery("");setSearchResults(null);const sp=spaces.find(s=>s.id===f.spaceId);const inst={id:f.instanceId,name:f.instanceName,spaceId:f.spaceId};navigate("dashboard",sp,inst);}}>
                      <span>{f.Name}</span><span className="iio-search-meta">{f.instanceName}</span>
                    </button>
                  ))}
                </>}
                {!searchResults.instances?.length&&!searchResults.iflows?.length&&(
                  <div className="iio-search-empty">{t("suche.keineErgebnisse")}</div>
                )}
              </div>
            )}
          </div>

          {/* Die Verbindungsanzeige stand fest auf "Verbunden" und war an keinen
              Zustand gebunden - sie sagte nichts aus und ist entfallen. */}
          <div inert={compactViewport&&mobileNavOpen?"":undefined} className="iio-topbar-right">
            <button type="button" className="iio-icon-btn" onClick={toggleTheme} title={t("benutzer.themaWechseln")}>
              {theme==="dark"
                ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
                : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>}
            </button>
            <button type="button" className="iio-icon-btn" onClick={logout} title={t("benutzer.abmelden")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </button>
          </div>
        </header>

        {/* === MAIN === */}
        <main inert={compactViewport&&mobileNavOpen?"":undefined} className={"iio-main"+(page==="pipeline"||page==="transport"||page==="transport-v2"||page==="ai-builder"||page==="ai-insights"||page==="doku"?" iio-main--full":"")+(page==="approvals"?" promotion-main":"")}>
          {brandingError&&<div role="alert" style={{display:"flex",gap:16,alignItems:"center",flexWrap:"wrap",padding:16,color:"var(--iio-text-1)",borderBottom:"1px solid var(--iio-border)"}}>
            <span>{t("branding.ladenFehlgeschlagen")}</span>
            <button type="button" className="iio-btn iio-btn--secondary" onClick={reloadBranding}>{t("branding.erneutLaden")}</button>
          </div>}
          {showSettings&&<SettingsModal onClose={()=>{setShowSettings(false);requestAnimationFrame(()=>{const target=window.matchMedia("(max-width: 900px)").matches?document.querySelector(".iio-topbar-mobile-toggle"):sbUserRef.current;target?.focus({preventScroll:true});});}} user={user} onUpdate={u=>setUser(prev=>({...prev,...u,hasLlmConfig:true}))} onBrandingUpdate={reloadBranding}/>}
          {page==="spaces"&&<SpacesHome key={user.id} user={user} onBrandingChange={reloadBranding} spaces={spaces} loading={initialSpacesLoading} error={spacesError} cicdStatus={cicdStatus} onRefresh={loadSpaces} onOpenSpace={(s,inst)=>{if(inst){navigate("dashboard",s,inst);}else{navigate("space",s,null);}}}/>}
          {page==="space"&&activeSpace&&<SpaceDetail key={user.id} onInstanceUpdated={onInstanceUpdated} space={activeSpace} cicdStatus={cicdStatus} onBack={()=>{navigate("spaces",null,null);loadSpaces();}} onSpaceUpdated={updated=>{setActiveSpace(s=>({...s,...updated}));loadSpaces();}} onOpenInstance={(i,flowName)=>{setTargetFlowName(flowName||null);navigate("dashboard",undefined,i);}}/>}
          {page==="space"&&!activeSpace&&<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"50vh",color:"var(--iio-text-3)"}}>{t("gemeinsam:zustand.laedt")}</div>}
          {page==="dashboard"&&activeInst&&<Dashboard instance={activeInst} spaceId={activeSpace?.id} onBack={()=>{setTargetFlowName(null);navigate("space");}} targetFlowName={targetFlowName} onClearTarget={()=>setTargetFlowName(null)} targetTab={targetTab} onClearTargetTab={()=>setTargetTab(null)}/>}
          {page==="dashboard"&&!activeInst&&<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"50vh",color:"var(--iio-text-3)"}}>{t("gemeinsam:zustand.laedt")}</div>}
          {page==="ai-builder"&&<AiIflowBuilder/>}
          {page==="alerting"&&<AlertingPage spaces={spaces} onNavigate={navigate}/>}
          {page==="audit"&&<AuditLog/>}
          {page==="approvals"&&<TransportApprovals user={user} spaces={spaces}/>}
          {page==="pastebin"&&<PastebinPage/>}
          {page==="pd"&&<PartnerDirectory spaces={spaces} preselectedInstId={pdPreselectedInst} preselectedPid={pdPreselectedPid}/>}
          {page==="pixel-art"&&<PixelArt/>}
          {page==="doku"&&<DokuPage key={user.id} userId={user.id} spaces={spaces} branding={branding}/>}
          {page==="transport-v2"&&<TransportV2Page spaces={spaces} user={user}/>}
          {page==="pipeline"&&<PipelineTopology spaces={spaces} user={user} onNavigate={navigate}/>}
          {page==="benutzer-anlegen"&&<BenutzerAnlegen user={user} spaces={spaces} onDone={()=>navigate("spaces",null,null)}/>}
          {page==="benutzer-verwalten"&&<BenutzerVerwalten user={user} spaces={spaces}
            onUserUpdate={updated=>setUser(current=>current?.id===updated.id ? {...current,...updated} : current)}/>}
          {page==="cicd-setup"&&<CicdSetup key={user?.id} user={user} spaces={spaces} onCicdRefresh={loadCicdStatus}/>}
          {/* onCicdRefresh: nach dem Loesen einer Bindung im Monitor stimmt die Markierung an den
              Spaces nicht mehr. Sie wird sonst nur einmal beim Start geladen und bliebe stehen. */}
          {page==="cicd-pipeline"&&<CicdPipeline user={user} spaces={spaces} onCicdRefresh={loadCicdStatus}/>}
          {page==="ai-insights"&&<AiInsights/>}
          {page==="monitoring"&&<Monitoring user={user}/>}
          {page==="invite"&&inviteToken&&<InviteAccept token={inviteToken} onDone={()=>{navigate("spaces",null,null);loadSpaces();}}/>}
        </main>

        {/* === FOOTER === */}
        <footer inert={compactViewport&&mobileNavOpen?"":undefined} className="iio-footer">
          <span>abat AG · 2026</span>
          <span className="iio-sep">·</span>
          <span>{branding.title||"Integration in One"} · v2.0</span>
          <div className="iio-footer-right">
            <span>{t("fuss.hilfe")}</span>
          </div>
        </footer>

      </div>
        {canUseAssistant&&<ChatBubble open={chatOpen} onClick={()=>setChatOpen(o=>!o)} />}
    </div>
      {canUseAssistant && <>
        <ChatPanel key={user.id} open={chatOpen} onClose={()=>setChatOpen(false)} spaces={spaces} activeInst={activeInst} />
      </>}
    </ThemeCtx.Provider>
    </ThemeProvider>
    </ErrorBoundary>
    </QueryClientProvider>
  );
}
